'use client'

import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react'

interface VideoPlayerRef {
  id: string
  play: () => Promise<void>
  pause: () => Promise<void>
  isPlaying: boolean
}

interface VideoManagerContextType {
  registerPlayer: (id: string, playerRef: VideoPlayerRef) => void
  unregisterPlayer: (id: string) => void
  notifyPlay: (playingId: string) => void
  notifyPause: (pausedId: string) => void
  pauseAllExcept: (activeId: string) => void
  pauseAll: () => void
}

const VideoManagerContext = createContext<VideoManagerContextType | undefined>(undefined)

export const useVideoManager = () => {
  const context = useContext(VideoManagerContext)
  if (!context) {
    // Return no-op functions if context is not available
    return {
      registerPlayer: () => {},
      unregisterPlayer: () => {},
      notifyPlay: () => {},
      notifyPause: () => {},
      pauseAllExcept: () => {},
      pauseAll: () => {}
    }
  }
  return context
}

interface VideoManagerProviderProps {
  children: ReactNode
}

export const VideoManagerProvider: React.FC<VideoManagerProviderProps> = ({ children }) => {
  const playersRef = useRef<Map<string, VideoPlayerRef>>(new Map())

  const registerPlayer = useCallback((id: string, playerRef: VideoPlayerRef) => {
    playersRef.current.set(id, playerRef)
  }, [])

  const unregisterPlayer = useCallback((id: string) => {
    playersRef.current.delete(id)
  }, [])

  const notifyPlay = useCallback((playingId: string) => {
    const player = playersRef.current.get(playingId)
    if (player) {
      playersRef.current.set(playingId, { ...player, isPlaying: true })
    }
  }, [])

  const notifyPause = useCallback((pausedId: string) => {
    const player = playersRef.current.get(pausedId)
    if (player) {
      playersRef.current.set(pausedId, { ...player, isPlaying: false })
    }
  }, [])

  const pauseAllExcept = useCallback(async (activeId: string) => {
    const currentPlayers = Array.from(playersRef.current.values())
    
    for (const player of currentPlayers) {
      if (player.id !== activeId && player.isPlaying) {
        try {
          await player.pause()
        } catch (error) {
          console.warn(`Failed to pause video ${player.id}:`, error)
        }
      }
    }
  }, [])

  const pauseAll = useCallback(async () => {
    const currentPlayers = Array.from(playersRef.current.values())
    
    for (const player of currentPlayers) {
      if (player.isPlaying) {
        try {
          await player.pause()
        } catch (error) {
          console.warn(`Failed to pause video ${player.id}:`, error)
        }
      }
    }
  }, [])

  return (
    <VideoManagerContext.Provider value={{
      registerPlayer,
      unregisterPlayer,
      notifyPlay,
      notifyPause,
      pauseAllExcept,
      pauseAll
    }}>
      {children}
    </VideoManagerContext.Provider>
  )
}
